import React from 'react'

interface ButtonProps {
  children: React.ReactNode
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  disabled?: boolean
  loading?: boolean
  onClick?: () => void
  type?: 'button' | 'submit' | 'reset'
  className?: string
  fullWidth?: boolean
  'aria-label'?: string
  autoFocus?: boolean
}

export function Button({
  children,
  variant = 'secondary',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  className = '',
  fullWidth = false,
  'aria-label': ariaLabel,
  autoFocus,
}: ButtonProps) {
  const base = [
    'inline-flex items-center justify-center rounded-eg-md font-body font-semibold',
    'transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed',
  ]

  const sizes: Record<string, string> = {
    sm: 'px-3 py-1.5 text-eg-sm',
    md: 'px-4 py-2 text-eg-md',
    lg: 'px-6 py-3 text-eg-md',
    xl: 'px-8 py-4 text-eg-lg',
  }

  const variants: Record<string, string> = {
    primary:
      'bg-eg-accent text-white hover:bg-eg-accent-hover focus:ring-eg-accent',
    secondary:
      'bg-eg-surface border border-eg-border text-eg-text hover:bg-eg-elevated focus:ring-eg-border',
    ghost:
      'bg-transparent text-eg-text-secondary hover:bg-eg-elevated focus:ring-eg-border',
    danger:
      'bg-eg-danger text-white hover:bg-eg-danger-hover focus:ring-eg-danger',
  }

  const classes = [
    ...base,
    sizes[size] || sizes.md,
    variants[variant] || variants.secondary,
    fullWidth ? 'w-full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled || loading}
      onClick={onClick}
      aria-label={ariaLabel}
      autoFocus={autoFocus}
    >
      {loading ? (
        <span className="flex items-center gap-2">
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8H4z"
            />
          </svg>
          {children}
        </span>
      ) : (
        children
      )}
    </button>
  )
}
